import { Company } from 'helpers/hooks/usePartnerCustomers/usePartnerCustomers';
import { Autocomplete } from '@atoms';
import { useURLparams, usePartnerCustomers } from '@hooks';

interface CompanyOption {
  id: number;
  legalName: string;
}

interface SelectCustomerProps {
  disabled?: boolean;
  placeholder?: string;
  customOptions?: Company[];
  value?: string;
  onChange?: (customerId: string | null) => void;
  onClear?: () => void;
}

/**
 * This component renders a customer select Autocomplete using the Autocomplete component.
 * @component SelectCustomer
 */
const SelectCustomer: React.FC<SelectCustomerProps> = ({
  disabled = false,
  placeholder = 'CLIENTS:SELECT_COMPANY',
  customOptions,
  value: controlledValue,
  onChange: controlledOnChange,
  onClear,
}: SelectCustomerProps): JSX.Element => {
  const { data, isLoading } = usePartnerCustomers();
  const { params, setURLparams } = useURLparams();
  const isControlled = controlledOnChange !== undefined;
  const value = isControlled
    ? controlledValue || ''
    : 'customerId' in params && params?.customerId
    ? String(params.customerId)
    : '';
  const options = customOptions ? customOptions : data;

  /**
   * Handles the change event of the Autocomplete component
   * @function handleChange
   */
  const handleChange = (_event: React.SyntheticEvent, selectedOption: CompanyOption | null) => {
    if (isControlled) {
      controlledOnChange(selectedOption ? String(selectedOption.id) : null);
      return;
    }
    if (selectedOption) {
      setURLparams({ customerId: String(selectedOption.id) });
    }
  };

  /**
   * Handles the label to be displayed in the Autocomplete component
   * @function getOptionLabel
   */
  const getOptionLabel = (value: { label: string }): string =>
    typeof value === 'object'
      ? value.label ?? ''
      : options?.find(({ id }) => id === Number(value))?.legalName || '';

  return (
    <Autocomplete
      disabled={disabled}
      isLoading={isLoading}
      onChange={handleChange}
      value={value}
      items={options || []}
      placeholder={placeholder}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={({ id }: { id: string }, value: string) => id === value}
      parseOption={({ id, legalName }: CompanyOption) => ({ id, label: legalName })}
      onClear={onClear}
      clearIcon={Boolean(onClear) || isControlled}
    />
  );
};

export default SelectCustomer;
